import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, Loader2, Lock, Mail, PenTool, AlertCircle } from 'lucide-react';
import { useWritingPages } from '@/hooks/useWritingPages';
import { useTurnstile } from '@/hooks/useTurnstile';
import WritingPagesInput from '@/components/WritingPagesInput';
import {
  AssessmentTextBlock,
  ExtractedTextToggle,
  FluencyScoreGrid,
  GradeWithJustification,
  ProvisionalGradeNote,
} from '@/components/assessment/ResultParts';
import { getMimeType, mimeToExt } from '@/lib/writingFiles';
import type { FluencyScore } from '@/lib/fluency';
import { mapGrade } from '@/lib/assessment';
import { setSignupPrefill } from '@/lib/signupPrefill';
import { trackEvent } from '@/lib/analytics';

const YEAR_GROUPS = ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];
const ROLES = [
  { value: 'class_teacher', label: 'Class teacher' },
  { value: 'english_lead', label: 'English lead' },
  { value: 'slt', label: 'SLT' },
  { value: 'other', label: 'Other' },
];
const MAX_PAGES = 2;

// Must match MARKETING_CONSENT_TEXT in supabase/functions/free-sample-assessment/index.ts,
// which is what's stored with the consent timestamp.
const MARKETING_CONSENT_TEXT =
  'Email me occasional writing assessment tips and WriteAssess updates. Unsubscribe any time.';

const PROGRESS_MESSAGES = [
  'Reading the handwriting…',
  'Checking against the exemplification standards…',
];

interface SampleResult {
  grade: string | null;
  assessment: string;
  extracted_text: string | null;
  fluency_score: FluencyScore | null;
}

type Refusal = 'duplicate' | 'existing_account' | 'rate_limited' | 'disposable_email';

type Outcome =
  | { kind: 'result'; result: SampleResult }
  | { kind: 'emailed'; email: string }
  | { kind: 'refused'; reason: Refusal; message: string };

const LOCKED_FEATURES = [
  { title: 'Accept or override the grade', line: 'Create a free account to record your judgement against the pupil.' },
  { title: 'Ask AI about this assessment', line: 'Create a free account to ask follow-up questions about the judgement.' },
  { title: 'Save to a pupil and track progress', line: 'Create a free account to build each pupil\'s record over time.' },
  { title: 'Generate a report comment', line: 'Create a free account to turn assessments into parent-friendly report comments.' },
];

export default function TryFree() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const turnstile = useTurnstile();
  const resultRef = useRef<HTMLDivElement>(null);

  const { pages, fileError, setFileError, openFilePicker, removePage, clearPersisted } = useWritingPages({
    idbName: 'wa_try',
    maxPages: MAX_PAGES,
    onFileError: (message) => toast({ title: 'Could not add file', description: message, variant: 'destructive' }),
  });

  const [yearGroup, setYearGroup] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [noIdentifyingDetails, setNoIdentifyingDetails] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [progressIdx, setProgressIdx] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  // A field's error goes as soon as the visitor edits that field, so it never describes an old value.
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  // A message about the upload (e.g. "couldn't read any writing") is stale once the pages change.
  useEffect(() => {
    setFormError(null);
  }, [pages.length]);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [extractedOpen, setExtractedOpen] = useState(false);

  // utm_* are kept for the lead's source; read once so they survive any URL changes.
  const [utm] = useState(() => ({
    source: searchParams.get('utm_source')?.slice(0, 100) ?? '',
    campaign: searchParams.get('utm_campaign')?.slice(0, 100) ?? '',
  }));

  useEffect(() => {
    if (!submitting) return;
    setProgressIdx(0);
    const t = setTimeout(() => setProgressIdx(1), 7000);
    return () => clearTimeout(t);
  }, [submitting]);

  useEffect(() => {
    if (outcome && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [outcome]);

  const goToSignUp = () => {
    setSignupPrefill({ email: email.trim(), firstName: firstName.trim() });
    navigate('/auth?plan=individual');
  };
  const goToSignIn = () => {
    setSignupPrefill({ email: email.trim() });
    navigate('/auth');
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (pages.length === 0) next.file = 'Please add a photo or PDF of the writing.';
    if (!yearGroup) next.yearGroup = 'Please choose the year group.';
    if (!firstName.trim()) next.firstName = 'Please enter your first name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Please enter a valid email address.';
    if (!noIdentifyingDetails) next.noIdentifyingDetails = 'Please confirm the writing has no names or identifying details on it.';
    if (!turnstile.token) next.captcha = 'Please complete the security check.';
    setErrors(next);
    if (next.file) setFileError(next.file);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setSubmitting(true);
    trackEvent('sample_assessment_started');

    const form = new FormData();
    pages.forEach((page, i) => {
      // Some Android camera captures have no type or extension; send a typed, named file.
      const mime = getMimeType(page.file);
      const hasExt = page.file.name.includes('.');
      const name = hasExt ? page.file.name : `page-${i + 1}.${mimeToExt(mime)}`;
      form.append('file', new File([page.file], name, { type: mime }));
    });
    form.append('yearGroup', yearGroup);
    form.append('firstName', firstName.trim());
    form.append('email', email.trim());
    if (role) form.append('role', role);
    if (schoolName.trim()) form.append('schoolName', schoolName.trim());
    form.append('noIdentifyingDetails', String(noIdentifyingDetails));
    form.append('marketingConsent', String(marketingConsent));
    form.append('turnstileToken', turnstile.token);
    if (utm.source) form.append('utmSource', utm.source);
    if (utm.campaign) form.append('utmCampaign', utm.campaign);

    try {
      const { data, error } = await supabase.functions.invoke('free-sample-assessment', { body: form });

      if (error) {
        let body: { code?: string; error?: string } = {};
        try {
          body = await (error as { context?: Response }).context?.json?.() ?? {};
        } catch { /* body not JSON or already consumed */ }
        const code = body.code ?? '';
        const message = body.error ?? 'Something went wrong. Please try again in a few minutes.';

        if (code === 'duplicate' || code === 'existing_account' || code === 'rate_limited' || code === 'disposable_email') {
          trackEvent('sample_assessment_refused', { reason: code });
          if (code === 'disposable_email') {
            setErrors((prev) => ({ ...prev, email: message }));
          } else {
            setOutcome({ kind: 'refused', reason: code, message });
          }
        } else {
          // unreadable, captcha, email_failed, server errors: they can fix it and try again.
          setFormError(message);
        }
        return;
      }

      clearPersisted();
      const emailType = data?.email_type === 'personal' ? 'personal' : 'work';
      if (emailType === 'work' && data?.result) {
        const result = data.result as SampleResult;
        trackEvent('generate_lead', {
          grade: result.grade ?? 'none',
          email_type: 'work',
          marketing_consent: marketingConsent,
        });
        setOutcome({ kind: 'result', result });
      } else {
        trackEvent('generate_lead', { email_type: 'personal', marketing_consent: marketingConsent });
        setOutcome({ kind: 'emailed', email: email.trim() });
      }
    } catch {
      setFormError('Something went wrong. Please check your connection and try again.');
    } finally {
      turnstile.reset();
      setSubmitting(false);
    }
  };

  const signUpCtas = (
    <div className="flex flex-col sm:flex-row gap-3">
      <Button onClick={goToSignUp} className="sm:flex-1">Start free</Button>
      <Button variant="outline" asChild className="sm:flex-1">
        <Link to="/pricing">See plans</Link>
      </Button>
    </div>
  );

  const showForm = !outcome;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="Try WriteAssess free"
        description="Upload one piece of pupil writing and get a free provisional WTS, EXS or GDS judgement against the KS1/KS2 exemplification standards. No account needed."
        canonical="/try"
      />

      <header className="border-b bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">WriteAssess</span>
          </Link>
          <Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Sign in
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="border-b bg-muted/30">
          <div className="max-w-2xl mx-auto px-6 py-10">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Try WriteAssess free</h1>
            <p className="mt-3 text-lg text-muted-foreground">
              Upload one piece of pupil writing and get a provisional best-fit judgement (WTS, EXS or GDS) with the reasons behind it. No account needed.
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-10 space-y-6">
          {outcome?.kind === 'refused' && (
            <div ref={resultRef}>
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <p className="text-sm">{outcome.message}</p>
                  </div>
                  {outcome.reason === 'existing_account' ? (
                    <Button onClick={goToSignIn}>Sign in</Button>
                  ) : (
                    signUpCtas
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {showForm && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your free sample assessment</CardTitle>
                <CardDescription>One piece of writing, one or two pages. Your photo is deleted straight after it's assessed.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <WritingPagesInput
                    label="Pupil's writing"
                    hint="Take a photo, choose from camera roll, or upload a PDF · 1 or 2 pages · 20 MB each"
                    pages={pages}
                    maxPages={MAX_PAGES}
                    error={fileError}
                    onAdd={openFilePicker}
                    onRemove={removePage}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="try-year">Year group</Label>
                    <Select value={yearGroup} onValueChange={(v) => { setYearGroup(v); clearError('yearGroup'); }}>
                      <SelectTrigger id="try-year" className={errors.yearGroup ? 'border-destructive' : ''}>
                        <SelectValue placeholder="Select year group..." />
                      </SelectTrigger>
                      <SelectContent>
                        {YEAR_GROUPS.map((yg) => (
                          <SelectItem key={yg} value={yg}>{yg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.yearGroup && <p className="text-sm text-destructive">{errors.yearGroup}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="try-first-name">Your first name</Label>
                      <Input
                        id="try-first-name"
                        autoComplete="given-name"
                        value={firstName}
                        maxLength={100}
                        onChange={(e) => { setFirstName(e.target.value); clearError('firstName'); }}
                        className={errors.firstName ? 'border-destructive' : ''}
                      />
                      {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="try-email">Your email</Label>
                      <Input
                        id="try-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        maxLength={254}
                        onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
                        className={errors.email ? 'border-destructive' : ''}
                      />
                      {errors.email
                        ? <p className="text-sm text-destructive">{errors.email}</p>
                        : <p className="text-xs text-muted-foreground">Using your school email? You'll see your result straight away.</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="try-role">Your role <span className="text-muted-foreground font-normal">(optional)</span></Label>
                      <Select value={role} onValueChange={setRole}>
                        <SelectTrigger id="try-role">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="try-school">School name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                      <Input
                        id="try-school"
                        autoComplete="organization"
                        value={schoolName}
                        maxLength={200}
                        onChange={(e) => setSchoolName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-1">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="try-no-pii"
                        checked={noIdentifyingDetails}
                        onCheckedChange={(v) => { setNoIdentifyingDetails(v === true); clearError('noIdentifyingDetails'); }}
                        className="mt-0.5"
                      />
                      <div>
                        <label htmlFor="try-no-pii" className="text-sm leading-snug cursor-pointer">
                          This writing has no pupil names or other identifying details on it, and I'm allowed to share it.
                        </label>
                        {errors.noIdentifyingDetails && <p className="text-sm text-destructive mt-1">{errors.noIdentifyingDetails}</p>}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="try-marketing"
                        checked={marketingConsent}
                        onCheckedChange={(v) => setMarketingConsent(v === true)}
                        className="mt-0.5"
                      />
                      <label htmlFor="try-marketing" className="text-sm leading-snug cursor-pointer">
                        {MARKETING_CONSENT_TEXT}
                      </label>
                    </div>
                  </div>

                  <div>
                    <div ref={turnstile.containerRef} />
                    {errors.captcha && <p className="text-sm text-destructive mt-1">{errors.captcha}</p>}
                  </div>

                  {formError && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                      <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      <span>{formError}</span>
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {PROGRESS_MESSAGES[progressIdx]}</>
                    ) : (
                      'Assess my sample'
                    )}
                  </Button>
                  {submitting && (
                    <p className="text-xs text-muted-foreground text-center">This usually takes 20 to 40 seconds. Please keep this page open.</p>
                  )}

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    We use your name and email to send you your result. We only send you marketing emails if you tick the box above, except that if you use a work email address we may send occasional emails about relevant WriteAssess products, always with an unsubscribe link. Your photo is deleted straight after it's assessed and we don't keep the assessment text. You can opt out at any time using the link in any email or by <Link to="/contact" className="underline hover:text-foreground">contacting us</Link>. Read our <Link to="/privacy" className="underline hover:text-foreground">privacy policy</Link>.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}

          {outcome?.kind === 'emailed' && (
            <div ref={resultRef}>
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <p className="text-sm">
                      We've emailed your result to <span className="font-medium">{outcome.email}</span>. Check your inbox (and spam folder).
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Want to assess more writing, save results to pupils and track progress? Create a free account.
                  </p>
                  {signUpCtas}
                </CardContent>
              </Card>
            </div>
          )}

          {outcome?.kind === 'result' && (() => {
            const { result } = outcome;
            const grade = mapGrade(result.grade);
            return (
              <div ref={resultRef} className="space-y-6">
                <Card className="border-primary/30 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      Assessment Result
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Suggested grade</span>
                      <div className="mt-1">
                        {grade ? (
                          <GradeWithJustification grade={grade} justification={result.assessment} />
                        ) : (
                          <p className="text-xl font-bold">No grade suggested</p>
                        )}
                      </div>
                      <ProvisionalGradeNote />
                    </div>

                    <AssessmentTextBlock text={result.assessment} />

                    {result.fluency_score && <FluencyScoreGrid score={result.fluency_score} />}

                    {result.extracted_text && (
                      <ExtractedTextToggle
                        text={result.extracted_text}
                        open={extractedOpen}
                        onToggle={() => setExtractedOpen((o) => !o)}
                      />
                    )}

                    {/* Account-only features: visible but locked. Nothing here calls the API. */}
                    <div className="border-t pt-4 space-y-3">
                      {LOCKED_FEATURES.map(({ title, line }) => (
                        <div key={title} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <Button
                            type="button"
                            variant="outline"
                            disabled
                            aria-disabled="true"
                            className="sm:w-64 justify-start opacity-60"
                          >
                            <Lock className="mr-2 h-4 w-4" />
                            {title}
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            <button type="button" onClick={goToSignUp} className="text-primary hover:underline">
                              {line}
                            </button>
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <p className="text-sm">
                      We've also emailed a copy to <span className="font-medium">{email.trim()}</span>. To keep assessing, create a free account.
                    </p>
                    {signUpCtas}
                  </CardContent>
                </Card>
              </div>
            );
          })()}
        </div>
      </main>

      <footer className="border-t mt-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground">
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
          </div>
          <p>© {new Date().getFullYear()} WriteAssess. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
